import { ref, reactive, computed } from 'vue';
import { fetchShopConfigurationsNest } from '@/composables/shop/ShopsConfiguration';
import { getLocalStorageElement, setLocalStorageElement } from '@/helpers/LocalStorage';

const STORAGE_KEY = 'spm_demo_mode';
const OVERRIDES_STORAGE_KEY = 'spm_demo_overrides';
const CAMPAIGNS_STORAGE_KEY = 'spm_demo_campaigns';

export interface DemoOverrides {
  name: string;
  url: string;
  logo_url: string;
  push_icon_url: string;
}

export interface DemoCampaigns {
  automationIds: number[];
  bulkIds: number[];
  templateIds: number[];
}

const EMPTY_OVERRIDES: DemoOverrides = {
  name: '',
  url: '',
  logo_url: '',
  push_icon_url: '',
};

const EMPTY_CAMPAIGNS: DemoCampaigns = {
  automationIds: [],
  bulkIds: [],
  templateIds: [],
};

/** Whether the current shop has sandbox_mode capability */
const isSandboxAccount = ref(false);

/** Whether the user has actively toggled demo mode on */
const isDemoModeActive = ref(false);

/** Demo overrides for shop variables */
const demoOverrides = reactive<DemoOverrides>({ ...EMPTY_OVERRIDES });

/** Demo campaign selection */
const demoCampaigns = reactive<DemoCampaigns>({ ...EMPTY_CAMPAIGNS });

/** Whether the settings dialog is visible */
const isDemoSettingsVisible = ref(false);

/**
 * Returns only the non-empty overrides as a plain object.
 * Used for sending to the backend via header.
 */
const activeDemoOverrides = computed<Partial<DemoOverrides> | null>(() => {
  if (!isDemoModeActive.value) return null;
  const result: Partial<DemoOverrides> = {};
  let hasAny = false;
  for (const [key, value] of Object.entries(demoOverrides)) {
    if (value.trim()) {
      result[key as keyof DemoOverrides] = value.trim();
      hasAny = true;
    }
  }
  return hasAny ? result : null;
});

/**
 * Restore demo state from localStorage using shop-qualified keys.
 * Must be called when UserState is initialized (activeShop is set).
 */
function restoreFromLocalStorage(): void {
  const stored = getLocalStorageElement(STORAGE_KEY);
  isDemoModeActive.value = stored === 'true';

  if (isDemoModeActive.value) {
    const storedOverrides = getLocalStorageElement(OVERRIDES_STORAGE_KEY);
    if (storedOverrides) {
      try {
        Object.assign(demoOverrides, { ...EMPTY_OVERRIDES, ...JSON.parse(storedOverrides) });
      } catch {
        // ignore
      }
    }

    const storedCampaigns = getLocalStorageElement(CAMPAIGNS_STORAGE_KEY);
    if (storedCampaigns) {
      try {
        Object.assign(demoCampaigns, { ...EMPTY_CAMPAIGNS, ...JSON.parse(storedCampaigns) });
      } catch {
        // ignore
      }
    }
  } else {
    Object.assign(demoOverrides, EMPTY_OVERRIDES);
    Object.assign(demoCampaigns, EMPTY_CAMPAIGNS);
  }
}

/**
 * Initialize demo mode state: fetch sandbox capability from API + restore from localStorage.
 * Call this on app init and on shop switch.
 * @param shopId - The active shop ID
 */
async function initDemoMode(shopId?: number): Promise<void> {
  if (!shopId) {
    isSandboxAccount.value = false;
    isDemoModeActive.value = false;
    Object.assign(demoOverrides, EMPTY_OVERRIDES);
    Object.assign(demoCampaigns, EMPTY_CAMPAIGNS);
    return;
  }

  const config = await fetchShopConfigurationsNest(shopId, ['sandbox_mode']);
  isSandboxAccount.value = config?.sandbox_mode === 'true';

  if (isSandboxAccount.value) {
    restoreFromLocalStorage();
  } else {
    // Not a sandbox account — reset everything
    isDemoModeActive.value = false;
    Object.assign(demoOverrides, EMPTY_OVERRIDES);
    Object.assign(demoCampaigns, EMPTY_CAMPAIGNS);
  }
}

/**
 * Toggle demo mode on/off and persist to localStorage
 */
function toggleDemoMode(): void {
  isDemoModeActive.value = !isDemoModeActive.value;
  setLocalStorageElement(STORAGE_KEY, String(isDemoModeActive.value));
  window.location.reload();
}

/**
 * Save demo overrides to localStorage
 */
function saveDemoOverrides(overrides: DemoOverrides): void {
  Object.assign(demoOverrides, overrides);
  setLocalStorageElement(OVERRIDES_STORAGE_KEY, JSON.stringify(overrides));
}

/**
 * Save demo campaign selection to localStorage
 */
function saveDemoCampaigns(campaigns: DemoCampaigns): void {
  Object.assign(demoCampaigns, campaigns);
  setLocalStorageElement(CAMPAIGNS_STORAGE_KEY, JSON.stringify(campaigns));
}

/**
 * Add a newly created item ID to the demo selection so it appears in filtered lists.
 */
function addDemoItem(type: 'automation' | 'bulk' | 'template', id: number): void {
  if (!isDemoModeActive.value || !id) return;

  const key = type === 'automation' ? 'automationIds' : type === 'bulk' ? 'bulkIds' : 'templateIds';
  if (!demoCampaigns[key].includes(id)) {
    demoCampaigns[key].push(id);
    setLocalStorageElement(CAMPAIGNS_STORAGE_KEY, JSON.stringify(demoCampaigns));
  }
}

function showDemoSettings(): void {
  isDemoSettingsVisible.value = true;
}

function hideDemoSettings(): void {
  isDemoSettingsVisible.value = false;
}

export {
  isSandboxAccount,
  isDemoModeActive,
  demoOverrides,
  demoCampaigns,
  isDemoSettingsVisible,
  activeDemoOverrides,
  initDemoMode,
  toggleDemoMode,
  saveDemoOverrides,
  saveDemoCampaigns,
  addDemoItem,
  showDemoSettings,
  hideDemoSettings,
};
